// ============================================================================
// Cocoon Nest · ReportingEngine.jsx  (Iter-312 · BI Isolation — IMMUTABLE)
// ----------------------------------------------------------------------------
// Master Rule: All Business-Intelligence math for the Branch / Brand Overview
// dashboards lives strictly in this file. Reporting math and notification
// snapshots are IMMUTABLE during UI tasks (see SYSTEM_RULES.md).
//
// Scope — this engine owns:
//   • Bali-timezone date helpers  (todayBali, addDays, firstOfMonthBali, …)
//   • Date-range presets           (PRESETS · rangeForPreset)
//   • Dashboard metric formatters  (formatSnapshotMoney, formatQuantity, …)
//   • Ranked-list sorters          (sortServices, sortCareTeam, sortCustomers)
//   • Appointments-by-day chart    (computeAppointmentsByDay)
//   • Cancellation display logic   (deriveCancellationLabel · deriveCancellationReason)
//   • Shared constants             (RANGE_LABELS · DAY_COLOR_MAP · WEEKDAYS
//                                   · DATE_RANGE_OPTIONS · CANCEL_STATUS_LABEL)
//
// Purity Contract: no React state, no I/O, no DOM. Callers fetch data
// (branch overview payload, catalog rows) and pass it in; the engine
// returns primitives or plain arrays. Applies globally to all 8 branches.
//
// Consumers (Iter-312):
//   • pages/staff/branch/BranchOverview.jsx
//   • pages/staff/branch/BranchOverviewPrint.jsx
//   • pages/staff/brand/BrandDashboard.jsx
//   • pages/staff/brand/BrandNotifications.jsx
// ============================================================================

import { formatIDR } from "@/lib/api";

// ---------------------------------------------------------------------------
// Bali (Asia/Makassar) day helpers
// ---------------------------------------------------------------------------

/** Today's Bali local date as YYYY-MM-DD. */
export function todayBali() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
}

/**
 * Add `days` (may be negative) to a YYYY-MM-DD Bali date.
 * Anchors at Bali NOON (UTC 04:00) so day-arithmetic + `toLocaleDateString`
 * can never straddle the UTC day boundary and read the wrong Bali date
 * (regression uncovered in iter-267 when "yesterday" was shifting to
 * two days ago after 04:00 Bali).
 */
export function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00+08:00`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
}

/** First-of-current-Bali-month, YYYY-MM-DD. */
export function firstOfMonthBali() {
  return `${todayBali().slice(0, 7)}-01`;
}

/** First-of-previous-Bali-month, YYYY-MM-DD. */
export function firstOfPrevMonthBali() {
  const [y, m] = todayBali().slice(0, 7).split("-").map(Number);
  const prev = m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 };
  return `${prev.y}-${String(prev.m).padStart(2, "0")}-01`;
}

/** Last day of the previous Bali month, YYYY-MM-DD. */
export function lastOfPrevMonthBali() {
  return addDays(firstOfMonthBali(), -1);
}

// ---------------------------------------------------------------------------
// Date-range presets
// ---------------------------------------------------------------------------

/** Preset order rendered by the Branch Overview picker. */
export const PRESETS = [
  { key: "today",       label: "Today" },
  { key: "yesterday",   label: "Yesterday" },
  { key: "this_month",  label: "This Month" },
  { key: "last_week",   label: "Last Week" },
  { key: "last_month",  label: "Last Month" },
  { key: "last_year",   label: "Last Year" },
  { key: "custom",      label: "Custom Range" },
];

/** Preset order rendered by the Brand Overview picker (last_month uses
 *  a full-calendar-month window; branch uses trailing-30-days). */
export const PRESETS_BRAND = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "last_week",  label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_year",  label: "Last Year" },
  { key: "custom",     label: "Custom Range" },
];

/** Human-readable labels for the snapshot email header. */
export const RANGE_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last_week: "Last Week",
  this_month: "This Month",
  last_month: "Last Month",
  this_year: "This Year",
  last_year: "Last Year",
};

/** Branch Overview picker resolves to {date_from, date_to}. */
export function rangeForPreset(preset, customFrom, customTo) {
  const today = todayBali();
  switch (preset) {
    case "today":      return { date_from: today, date_to: today };
    case "yesterday":  { const y = addDays(today, -1); return { date_from: y, date_to: y }; }
    case "this_month": return { date_from: firstOfMonthBali(), date_to: today };
    case "last_week":  return { date_from: addDays(today, -6),  date_to: today };
    case "last_month": return { date_from: addDays(today, -29), date_to: today };
    case "last_year":  return { date_from: addDays(today, -364), date_to: today };
    case "custom":     return { date_from: customFrom, date_to: customTo };
    default:           return { date_from: today, date_to: today };
  }
}

/** Brand Overview picker resolves to {date_from, date_to}. `last_month`
 *  uses a strict calendar-month window (1st → last day of prior month). */
export function rangeForBrandPreset(preset, customFrom, customTo) {
  const today = todayBali();
  switch (preset) {
    case "today":      return { date_from: today, date_to: today };
    case "yesterday":  { const y = addDays(today, -1); return { date_from: y, date_to: y }; }
    case "this_month": return { date_from: firstOfMonthBali(), date_to: today };
    case "last_week":  return { date_from: addDays(today, -6),  date_to: today };
    case "last_month": return { date_from: firstOfPrevMonthBali(), date_to: lastOfPrevMonthBali() };
    case "last_year":  return { date_from: addDays(today, -364), date_to: today };
    case "custom":     return { date_from: customFrom, date_to: customTo };
    default:           return { date_from: today, date_to: today };
  }
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** Format a money amount using the branch's currency (fallback: IDR). */
export function formatSnapshotMoney(currency, value) {
  const n = Number(value ?? 0);
  if ((currency || "IDR").toUpperCase() === "IDR") {
    return formatIDR(n);
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "IDR").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_e) {
    return formatIDR(n);
  }
}

// ---------------------------------------------------------------------------
// Sorters
// ---------------------------------------------------------------------------

/** Sort services by "quantity" (units performed) or "revenue" (net sales). */
export function sortServices(rows, mode) {
  const list = [...(rows || [])];
  list.sort((a, b) =>
    mode === "quantity" ? (b.quantity || 0) - (a.quantity || 0) : (b.revenue || 0) - (a.revenue || 0),
  );
  return list;
}

/** Sort Care Team rows by quantity or revenue. Same tie-breaker semantics as sortServices. */
export function sortCareTeam(rows, mode) {
  const list = [...(rows || [])];
  list.sort((a, b) =>
    mode === "quantity" ? (b.quantity || 0) - (a.quantity || 0) : (b.revenue || 0) - (a.revenue || 0),
  );
  return list;
}

/**
 * Sort Top Customers by spend or visits. `limit` caps the returned list
 * so the caller doesn't have to slice again (default: 10, matches the
 * legacy on-screen cap).
 */
export function sortCustomers(rows, mode, limit = 10) {
  const list = [...(rows || [])];
  list.sort((a, b) => {
    if (mode === "visits") {
      const dv = (b.visits || 0) - (a.visits || 0);
      return dv !== 0 ? dv : (b.spend || 0) - (a.spend || 0);
    }
    return (b.spend || 0) - (a.spend || 0);
  });
  return list.slice(0, limit);
}

// ---------------------------------------------------------------------------
// Appointments-by-day pie chart
// ---------------------------------------------------------------------------

/** Weekday → pastel colour map used by the pie chart AND the legend so
 *  slices + labels stay in lock-step regardless of which weekday is first. */
export const DAY_COLOR_MAP = {
  Sunday:    "#F9C0D0", // pink
  Monday:    "#C8E6C9", // light green
  Tuesday:   "#BBDEFB", // light blue
  Wednesday: "#D1C4E9", // soft purple
  Thursday:  "#FFECB3", // soft yellow
  Friday:    "#FFCCBC", // soft orange
  Saturday:  "#B2DFDB", // soft teal
};

/**
 * Compute the pie-chart payload from `data.appointments_by_day`.
 * Adds a `pct` property (rounded to 1 decimal) used for slice labels.
 */
export function computeAppointmentsByDay(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const total = rows.reduce((s, x) => s + (x.count || 0), 0) || 1;
  return rows.map((d) => ({
    ...d,
    pct: Math.round(((d.count || 0) / total) * 1000) / 10,
  }));
}

// ---------------------------------------------------------------------------
// Cancellation display
// ---------------------------------------------------------------------------

/** Human labels for a cancellation status pill. */
export const CANCEL_STATUS_LABEL = {
  cancelled: "Cancelled",
  no_show: "No-show",
  no_show_partial: "No-show (partial)",
};

/**
 * Preferred display name for a cancellation row.
 * Packages collapse to `package_name`; singles fall back to `service_name`.
 */
export function deriveCancellationLabel(row) {
  const isPackage = !!row?.is_package;
  if (isPackage) return row?.package_name || row?.service_name || "Package";
  return row?.service_name || "";
}

/**
 * Verbatim receptionist note when present, else the category label,
 * else a neutral fallback so operators never see a blank row.
 */
export function deriveCancellationReason(row) {
  const raw = (row?.reason || "").trim();
  if (raw) return raw;
  return row?.status === "no_show" ? "No-Show" : "Cancelled";
}

// ---------------------------------------------------------------------------
// Wizard constants shared with BrandNotifications
// ---------------------------------------------------------------------------

export const WEEKDAYS = [
  { key: 0, label: "Sun" },
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
];

export const DATE_RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
];
